import React from 'react'

import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TableSortLabel,
    TablePagination,
    makeStyles,
    Theme,
    createStyles,
    IconButton,
} from '@material-ui/core'
import { MoreVert } from '@material-ui/icons'

import { InternalInvoice } from '../../models'
import { currencyFormatter } from '../../helpers'
import { InvoiceStatusBadge } from '../../components/InternalInvoice/InvoiceStatusBadge'
import { formatDate } from '../../components/InternalInvoice/InvoiceDetailModal/helpers'

interface HeadCell {
    id: keyof InternalInvoice
    label: string
    numeric: boolean
    visible?: boolean
}

interface InvoiceTableProps {
    invoices: InternalInvoice[] | undefined
    orderBy: keyof InternalInvoice
    order: SortOrder
    onRequestSort: (property: keyof InternalInvoice, order: SortOrder) => void
    onViewInvoice: (invoice: InternalInvoice) => void
    tableHeight: string
    page: number
    rowsPerPage: number
    totalCount: number
    onPageChange: (event: unknown, newPage: number) => void
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void
}

// const paginationHeight = 94

const headCells: HeadCell[] = [
    { id: 'invoice_number', label: 'Invoice #', numeric: false },
    {
        id: 'billing_email',
        label: 'Invoice Email',
        numeric: false,
    },
    { id: 'status', label: 'Status', numeric: false },
    { id: 'issue_date', label: 'Issue Date', numeric: false },
    { id: 'due_date', label: 'Due Date', numeric: false },
    { id: 'total_amount', label: 'Billed', numeric: true },
    { id: 'balance_due', label: 'Balance Due', numeric: true },
]

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: (props: { tableHeight: string }) => props.tableHeight,
            minHeight: (props: { tableHeight: string }) => props.tableHeight,
        },
        container: {
            overflow: 'auto',
        },
        headerCell: {
            backgroundColor: theme.palette.background.paper,
            fontWeight: 'bold',
        },
        voidedRow: {
            backgroundColor: theme.palette.action.disabledBackground,
            textDecoration: 'line-through',
        },
        normalRow: {
            backgroundColor: 'inherit',
            textDecoration: 'none',
        },
        pagination: {
            borderTop: `1px solid ${theme.palette.divider}`,
            marginTop: theme.spacing(1),
            minHeight: 60,
        },
        statusChip: {
            fontWeight: 500,
            minWidth: 100,
            '& .MuiIcon-root': {
                color: 'inherit',
            },
            '& .MuiSvgIcon-root': {
                color: 'inherit',
            },
        },
        statusIcon: {
            width: 16,
            height: 16,
            color: 'inherit',
        },
    }),
)

export const BillingInvoiceTable: React.FC<InvoiceTableProps> = ({
    invoices,
    orderBy,
    order,
    onRequestSort,
    onViewInvoice,
    tableHeight,
    page,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const classes = useStyles({ tableHeight })

    const createSortHandler = (property: keyof InternalInvoice) => () => {
        // If we're already sorting by this field, toggle direction
        // Otherwise, start with ascending
        const newOrder =
            orderBy === property && order === 'asc' ? 'desc' : 'asc'
        onRequestSort(property, newOrder)
    }

    const invoiceLength = invoices?.length ?? 0

    const renderCellContent = (
        invoice: InternalInvoice,
        columnId: keyof InternalInvoice,
    ) => {
        switch (columnId) {
            case 'invoice_number':
                return invoice.invoice_number
            case 'billing_group_display_name':
                return (
                    invoice.billing_group.quickbooks_customer.display_name ||
                    'N/A'
                )
            case 'billing_email':
                return invoice.billing_group.invoice_email || 'N/A'
            case 'status':
                return (
                    <InvoiceStatusBadge
                        status={invoice.status}
                        voided={invoice.voided}
                        reviewed={invoice.reviewed}
                    />
                )
            case 'issue_date':
                return formatDate(invoice.issue_date)
            case 'due_date':
                return formatDate(invoice.due_date)
            case 'total_amount':
                return currencyFormatter.format(Number(invoice.total_amount))
            case 'balance_due':
                return currencyFormatter.format(Number(invoice.balance_due))
            default:
                return null
        }
    }

    return (
        <div className={classes.root}>
            <TableContainer component={Paper} className={classes.container}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    sortDirection={
                                        orderBy === headCell.id ? order : false
                                    }
                                    className={classes.headerCell}
                                >
                                    <TableSortLabel
                                        active={orderBy === headCell.id}
                                        direction={
                                            orderBy === headCell.id
                                                ? order
                                                : 'asc'
                                        }
                                        onClick={createSortHandler(headCell.id)}
                                    >
                                        {headCell.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                            <TableCell className={classes.headerCell}>
                                View Invoice
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody style={{ overflow: ' scroll' }}>
                        {invoices?.map((invoice) => {
                            return (
                                <TableRow
                                    key={invoice.id}
                                    className={
                                        invoice.voided
                                            ? classes.voidedRow
                                            : classes.normalRow
                                    }
                                    hover
                                >
                                    {headCells.map((headCell) => (
                                        <TableCell
                                            key={`${invoice.id}-${headCell.id}`}
                                        >
                                            {renderCellContent(
                                                invoice,
                                                headCell.id,
                                            )}
                                        </TableCell>
                                    ))}
                                    <TableCell>
                                        <IconButton
                                            onClick={(e) => {
                                                onViewInvoice(invoice)
                                                e.stopPropagation()
                                            }}
                                        >
                                            <MoreVert />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        {(!invoices || invoices.length === 0) && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">
                                    No invoices found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                className={classes.pagination}
                component="div"
                count={totalCount}
                page={page}
                onChangePage={onPageChange}
                onChangeRowsPerPage={onRowsPerPageChange}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
            />
        </div>
    )
}
